import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Button, Form, Modal } from "react-bootstrap";
import { OrgContext } from "./PortalLayout";
import { setLogout } from "../../redux/features/authSlice";
import { addSite, inviteUser, setUserStatus } from "../../services/orgService";
import { getNotifications, markNotificationsRead } from "../../services/authService";
import { errorToast, successToast } from "../Toasts/Toasts";

// The "More" tab and the pages under it: sites, users (admin), notifications,
// profile, sign out.

const ROLE_LABEL = {
  CUST_ADMIN: "Admin",
  CUST_MANAGER: "Manager",
  CUST_APPROVER: "Approver",
  CUST_ACCOUNTS: "Accounts",
};

export const PortalMore = () => {
  const { org, user } = useContext(OrgContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const signOut = () => {
    dispatch(setLogout());
    localStorage.clear();
    navigate("/");
  };
  return (
    <>
      <div className="portal-card">
        <div className="fw-semibold">{user?.name || "—"}</div>
        <div className="small text-muted">+{user?.phone} · {ROLE_LABEL[user?.role] || user?.role}</div>
        <div className="small text-muted">{org?.name}</div>
      </div>
      <div className="portal-card portal-list">
        <Link className="item d-block" to="/portal/sites">Sites</Link>
        {user?.role === "CUST_ADMIN" && <Link className="item d-block" to="/portal/users">Users</Link>}
        <Link className="item d-block" to="/portal/notifications">Notifications</Link>
        <Link className="item d-block" to="/portal/documents">Documents</Link>
      </div>
      <Button variant="outline-secondary" className="w-100" onClick={signOut}>Sign out</Button>
    </>
  );
};

export const PortalSites = () => {
  const { org, refreshOrg, user } = useContext(OrgContext);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", contactName: "", contactPhone: "", line1: "", city: "", pincode: "" });
  const canEdit = user?.role === "CUST_ADMIN";

  const save = async (e) => {
    e.preventDefault();
    try {
      const r = await addSite(org.id, {
        name: form.name,
        contactName: form.contactName,
        contactPhone: form.contactPhone,
        deliveryAddress: { line1: form.line1, city: form.city, pincode: form.pincode },
      });
      if (r?.success) { successToast("Site added"); setShow(false); refreshOrg(); }
      else errorToast(r?.message);
    } catch (err) { errorToast(err?.message || "Could not add the site"); }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Sites</h5>
        {canEdit && <Button size="sm" variant="success" onClick={() => setShow(true)}>+ Add</Button>}
      </div>
      <div className="portal-card portal-list">
        {org?.sites?.length ? org.sites.map((s) => (
          <div className="item" key={s.id}>
            <div className="fw-semibold">{s.name}</div>
            {s.deliveryAddress && <div className="small text-muted">{[s.deliveryAddress.line1, s.deliveryAddress.city, s.deliveryAddress.pincode].filter(Boolean).join(", ")}</div>}
            {s.contactName && <div className="small text-muted">{s.contactName} {s.contactPhone && `· +${s.contactPhone}`}</div>}
          </div>
        )) : <div className="portal-empty">No sites yet.</div>}
      </div>
      <Modal show={show} onHide={() => setShow(false)}>
        <Form onSubmit={save}>
          <Modal.Header closeButton><Modal.Title>Add a site</Modal.Title></Modal.Header>
          <Modal.Body>
            {[["name", "Site name", true], ["contactName", "Contact person"], ["contactPhone", "Contact mobile"], ["line1", "Address"], ["city", "City"], ["pincode", "Pincode"]].map(([k, label, req]) => (
              <Form.Group className="mb-2" key={k}>
                <Form.Label>{label}</Form.Label>
                <Form.Control required={!!req} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              </Form.Group>
            ))}
          </Modal.Body>
          <Modal.Footer><Button type="submit" variant="success">Save</Button></Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export const PortalUsers = () => {
  const { org, refreshOrg } = useContext(OrgContext);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", role: "CUST_MANAGER" });

  const invite = async (e) => {
    e.preventDefault();
    try {
      const r = await inviteUser(org.id, form);
      if (r?.success) { successToast("Invitation sent on WhatsApp"); setShow(false); setForm({ name: "", phone: "", role: "CUST_MANAGER" }); refreshOrg(); }
      else errorToast(r?.message);
    } catch (err) { errorToast(err?.message || "Could not invite"); }
  };
  const toggle = async (u) => {
    const status = u.status === "disabled" ? "active" : "disabled";
    try {
      const r = await setUserStatus(org.id, u.id, status);
      if (r?.success) refreshOrg(); else errorToast(r?.message);
    } catch (err) { errorToast(err?.message); }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Users</h5>
        <Button size="sm" variant="success" onClick={() => setShow(true)}>+ Invite</Button>
      </div>
      <div className="portal-card portal-list">
        {org?.users?.map((u) => (
          <div className="item d-flex justify-content-between align-items-center" key={u.id}>
            <div>
              <div className="fw-semibold">{u.name || "—"} <span className={`status-pill ${u.status}`}>{u.status}</span></div>
              <div className="small text-muted">+{u.phone} · {ROLE_LABEL[u.role] || u.role}</div>
            </div>
            <Button size="sm" variant="outline-secondary" onClick={() => toggle(u)}>{u.status === "disabled" ? "Enable" : "Disable"}</Button>
          </div>
        ))}
      </div>
      <Modal show={show} onHide={() => setShow(false)}>
        <Form onSubmit={invite}>
          <Modal.Header closeButton><Modal.Title>Invite a user</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-2"><Form.Label>Name</Form.Label><Form.Control value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Form.Group>
            <Form.Group className="mb-2"><Form.Label>Mobile (WhatsApp)</Form.Label><Form.Control required inputMode="numeric" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Role</Form.Label>
              <Form.Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer><Button type="submit" variant="success">Send invitation</Button></Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export const PortalNotifications = () => {
  const [rows, setRows] = useState([]);
  const load = async () => { try { setRows(await getNotifications()); } catch (e) { /* empty */ } };
  useEffect(() => {
    load().then(() => markNotificationsRead([]).catch(() => {}));
  }, []);
  useEffect(() => {
    const unread = rows.filter((r) => !r.readAt).map((r) => r.id);
    if (unread.length) markNotificationsRead(unread).catch(() => {});
  }, [rows]);
  return (
    <>
      <h5 className="mb-2">Notifications</h5>
      <div className="portal-card portal-list">
        {rows.length ? rows.map((n) => (
          <div className="item" key={n.id}>
            <div className={n.readAt ? "" : "fw-semibold"}>{n.title}</div>
            {n.body && <div className="small">{n.body}</div>}
            <div className="small text-muted">{new Date(n.createdAt).toLocaleString("en-IN")}</div>
          </div>
        )) : <div className="portal-empty">Nothing yet.</div>}
      </div>
    </>
  );
};

export const PortalComingSoon = ({ title }) => (
  <>
    <h5 className="mb-2">{title}</h5>
    <div className="portal-card"><div className="portal-empty">Coming in the next release.</div></div>
  </>
);
